import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'

/**
 * Neurological subsection adapter
 * Handles neurological assessment including PHQ2 scoring and seizure management (15 fields)
 */
export class NeurologicalAdapter extends BaseSubsectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Headache assessment
    const headaches = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHES
    )?.at(0)

    const headacheType = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHE_TYPE
    )

    // Seizure assessment
    const seizures = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURES
    )?.at(0)

    const seizureDate = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_DATE
    )?.at(0)

    const seizureType = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_TYPE
    )

    const seizurePrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_PRECAUTIONS
    )

    // General neurological status
    const neuroStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_NEURO_STATUS
    )

    const alteredAffect = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT
    )?.at(0)

    const alteredAffectFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT_FACTORS
    )

    // PHQ2 Depression Screening (Patient Health Questionnaire-2)
    const phq2Screening = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_SCREENING
    )?.at(0)

    const phq2Notes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_NOTES
    )?.at(0)

    // PHQ2 individual questions (scored 0-3 each)
    const interest = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_INTEREST
    )?.at(0)

    const depressed = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_DEPRESSED
    )?.at(0)

    // Caregiver assessment
    const caregiverDifficulty = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_CAREGIVER_DIFFICULTY
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RecertFormField.NEURO_HEADACHES]: headaches,
      [RecertFormField.NEURO_HEADACHE_TYPE]: headacheType,
      [RecertFormField.NEURO_SEIZURES]: seizures,
      [RecertFormField.NEURO_SEIZURE_DATE]: seizureDate,
      [RecertFormField.NEURO_SEIZURE_TYPE]: seizureType,
      [RecertFormField.NEURO_SEIZURE_PRECAUTIONS]: seizurePrecautions,
      [RecertFormField.NEURO_STATUS]: neuroStatus,
      [RecertFormField.NEURO_ALTERED_AFFECT]: alteredAffect,
      [RecertFormField.NEURO_ALTERED_AFFECT_FACTORS]: alteredAffectFactors,
      [RecertFormField.NEURO_PHQ2_SCREENING]: phq2Screening,
      [RecertFormField.NEURO_PHQ2_NOTES]: phq2Notes,
      [RecertFormField.NEURO_INTEREST]: interest,
      [RecertFormField.NEURO_DEPRESSED]: depressed,
      [RecertFormField.NEURO_CAREGIVER_DIFFICULTY]: caregiverDifficulty,
      [RecertFormField.NEURO_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RecertFormField.NEURO_HEADACHES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHES,
        value: this.getFormDataField(RecertFormField.NEURO_HEADACHES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_HEADACHE_TYPE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_HEADACHE_TYPE,
        value: this.getFormDataField(RecertFormField.NEURO_HEADACHE_TYPE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_SEIZURES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURES,
        value: this.getFormDataField(RecertFormField.NEURO_SEIZURES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_SEIZURE_DATE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_DATE,
        value: this.getFormDataField(RecertFormField.NEURO_SEIZURE_DATE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_SEIZURE_TYPE)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_TYPE,
        value: this.getFormDataField(RecertFormField.NEURO_SEIZURE_TYPE)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_SEIZURE_PRECAUTIONS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SEIZURE_PRECAUTIONS,
        value: this.getFormDataField(RecertFormField.NEURO_SEIZURE_PRECAUTIONS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_STATUS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_NEURO_STATUS,
        value: this.getFormDataField(RecertFormField.NEURO_STATUS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_ALTERED_AFFECT)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT,
        value: this.getFormDataField(RecertFormField.NEURO_ALTERED_AFFECT)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_ALTERED_AFFECT_FACTORS)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_ALTERED_AFFECT_FACTORS,
        value: this.getFormDataField(RecertFormField.NEURO_ALTERED_AFFECT_FACTORS)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_PHQ2_SCREENING)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_SCREENING,
        value: this.getFormDataField(RecertFormField.NEURO_PHQ2_SCREENING)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_PHQ2_NOTES)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_PHQ2_NOTES,
        value: this.getFormDataField(RecertFormField.NEURO_PHQ2_NOTES)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_INTEREST)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_INTEREST,
        value: this.getFormDataField(RecertFormField.NEURO_INTEREST)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_DEPRESSED)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_DEPRESSED,
        value: this.getFormDataField(RecertFormField.NEURO_DEPRESSED)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_CAREGIVER_DIFFICULTY)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_CAREGIVER_DIFFICULTY,
        value: this.getFormDataField(RecertFormField.NEURO_CAREGIVER_DIFFICULTY)
      })
    }

    if (this.formDataFieldExists(RecertFormField.NEURO_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_NEURO_SYSTEM_SUMMARY,
        value: this.getFormDataField(RecertFormField.NEURO_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
